import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import { LinkTo } from '@ember/routing';
import { VStack } from 'tio-ui/components/layout';
import Heading from 'tio-reimagined/components/heading';
import Text from 'tio-reimagined/components/text';
import { formatCurrency } from 'tio-reimagined/format';
import Chip from 'tio-reimagined/components/chip';
import Button from 'tio-reimagined/components/button';
import CircularProgress from 'tio-reimagined/components/circular-progress';
import StatCard from 'tio-reimagined/components/stat-card';
import PageHero from 'tio-reimagined/components/page-hero';
import {
  PhVault,
  PhShieldCheck,
  PhFileText,
  PhUploadSimple,
  PhDownloadSimple,
  PhCheckCircle,
  PhWarning,
  PhClock,
  PhCalendar,
  PhArrowRight,
  PhInfo,
  PhTarget,
} from 'ember-phosphor-icons';
import InfoRow from 'tio-reimagined/components/info-row';
import SectionHeader from 'tio-reimagined/components/section-header';
import type { PslfVaultSummary } from 'tio-employee/services/career-pathways-api';

interface PslfVaultIndexModel {
  vault: PslfVaultSummary;
}

interface PslfVaultIndexSignature {
  Args: {
    model: PslfVaultIndexModel;
  };
}

export default class CareerPathwaysPslfVaultIndex extends Component<PslfVaultIndexSignature> {
  @tracked showUploadModal = false;

  get vault() {
    return this.args.model.vault;
  }

  get progress() {
    return this.vault.progress;
  }

  get progressPercent() {
    return Math.round((this.progress.qualifyingPayments / 120) * 100);
  }

  get complianceColor() {
    if (this.vault.complianceScore >= 80) return 'emerald';
    if (this.vault.complianceScore >= 60) return 'amber';
    return 'red';
  }

  formatDate = (dateStr: string) => {
    return new Date(dateStr).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  getStatusColor = (status: string) => {
    switch (status) {
      case 'certified':
        return 'success';
      case 'submitted':
      case 'processing':
        return 'warning';
      case 'rejected':
        return 'danger';
      default:
        return 'default';
    }
  };

  <template>
    {{! template-lint-disable no-bare-strings }}
    <VStack class="data-audit w-full min-w-0 max-w-6xl mx-auto">
      {{! Hero Section }}
      <PageHero @theme="violet" @rounded="2xl">
        <:icon>
          <PhVault @size={{32}} />
          <Heading @level={{1}} class="text-white text-2xl md:text-3xl">
            PSLF Compliance Vault
          </Heading>
        </:icon>
        <:default>
          <Text class="text-violet-100 text-lg mb-6 max-w-2xl">
            Audit-proof documentation storage for your Public Service Loan Forgiveness journey.
            Track progress, store certifications, and stay on track for forgiveness.
          </Text>

          {{! Progress Ring }}
          <div class="flex items-center gap-6">
            <CircularProgress
              @value={{this.progress.qualifyingPayments}}
              @max={{120}}
              @color="emerald"
              @size="lg"
              @label="PSLF payment progress"
              class="[&_circle:first-child]:stroke-white/20"
            >
              <div class="text-center">
                <Text
                  class="text-2xl font-bold text-white"
                >{{this.progress.qualifyingPayments}}</Text>
                <Text class="text-xs text-violet-200">/120</Text>
              </div>
            </CircularProgress>
            <div>
              <Text class="text-white font-semibold">
                {{this.progress.paymentsRemaining}}
                payments remaining
              </Text>
              <Text class="text-violet-200 text-sm">
                Estimated forgiveness:
                {{this.formatDate this.progress.estimatedForgivenessDate}}
              </Text>
              <Text class="text-emerald-300 text-sm font-medium mt-1">
                Projected forgiveness:
                {{formatCurrency this.progress.projectedForgivenessAmount}}
              </Text>
            </div>
          </div>
        </:default>
      </PageHero>

      {{! Quick Stats }}
      <div class="grid grid-cols-2 md:grid-cols-4 gap-4 mt-6">
        <StatCard
          @icon={{PhShieldCheck}}
          @value="{{this.vault.complianceScore}}%"
          @label="Compliance Score"
          @trend={{if (gte this.vault.complianceScore 80) "up" "stable"}}
          @trendText={{if (gte this.vault.complianceScore 80) "Audit-ready" "Needs attention"}}
          @color={{this.complianceColor}}
        />
        <StatCard
          @icon={{PhFileText}}
          @value="{{this.vault.totalDocuments}}"
          @label="Documents Stored"
          @color="violet"
        />
        <StatCard
          @icon={{PhCheckCircle}}
          @value="{{this.vault.certifiedPeriods}}"
          @label="Certified Periods"
          @color="emerald"
        />
        <StatCard
          @icon={{PhCalendar}}
          @value={{this.formatDate this.progress.nextCertificationDue}}
          @label="Next ECF Due"
          @color="amber"
        />
      </div>

      {{! Risk Factors }}
      {{#if this.progress.riskFactors.length}}
        <div class="mt-6 space-y-3">
          {{#each this.progress.riskFactors as |risk|}}
            <div
              class="bg-{{if
                  (eq risk.severity 'high')
                  'red'
                  (if (eq risk.severity 'medium') 'amber' 'blue')
                }}-50 border border-{{if
                  (eq risk.severity 'high')
                  'red'
                  (if (eq risk.severity 'medium') 'amber' 'blue')
                }}-200 rounded-xl p-4"
            >
              <div class="flex items-start gap-3">
                <PhWarning
                  @size={{24}}
                  class="text-{{if
                      (eq risk.severity 'high')
                      'red'
                      (if (eq risk.severity 'medium') 'amber' 'blue')
                    }}-600 flex-shrink-0"
                />
                <div class="flex-1">
                  <Text
                    class="font-semibold text-{{if
                        (eq risk.severity 'high')
                        'red'
                        (if (eq risk.severity 'medium') 'amber' 'blue')
                      }}-800"
                  >
                    {{risk.title}}
                  </Text>
                  <Text
                    class="text-{{if
                        (eq risk.severity 'high')
                        'red'
                        (if (eq risk.severity 'medium') 'amber' 'blue')
                      }}-700 text-sm mt-1"
                  >
                    {{risk.description}}
                  </Text>
                </div>
                {{#if risk.actionUrl}}
                  <LinkTo @route={{risk.actionUrl}}>
                    <Button @intent="default" @size="sm" @appearance="outlined">
                      {{risk.action}}
                    </Button>
                  </LinkTo>
                {{/if}}
              </div>
            </div>
          {{/each}}
        </div>
      {{/if}}

      {{! Compliance Gaps }}
      {{#if this.vault.complianceGaps.length}}
        <div class="mt-6 bg-amber-50 border border-amber-200 rounded-xl p-4">
          <div class="flex items-center gap-2 mb-3">
            <PhInfo @size={{20}} class="text-amber-600" />
            <Text class="font-semibold text-amber-800">Compliance Gaps</Text>
          </div>
          <ul class="space-y-2">
            {{#each this.vault.complianceGaps as |gap|}}
              <li class="flex items-center gap-2 text-amber-700 text-sm">
                <PhClock @size={{16}} />
                {{gap}}
              </li>
            {{/each}}
          </ul>
        </div>
      {{/if}}

      <div class="grid grid-cols-1 lg:grid-cols-3 gap-6 mt-8">
        {{! Main Content - Documents }}
        <div class="lg:col-span-2">
          <div class="bg-white rounded-xl border border-gray-200 shadow-sm">
            <div class="flex items-center justify-between p-4 border-b border-gray-100">
              <Heading @level={{2}} class="text-lg font-semibold">Your Documents</Heading>
              <Button @intent="primary" @size="sm">
                <PhUploadSimple @size={{16}} class="mr-1" />
                Upload Document
              </Button>
            </div>

            <div class="divide-y divide-gray-100">
              {{#each this.vault.documents as |doc|}}
                <div class="p-4 flex items-center gap-4 hover:bg-gray-50">
                  <div class="p-2 bg-violet-100 rounded-lg">
                    <PhFileText @size={{24}} class="text-violet-600" />
                  </div>
                  <div class="flex-1 min-w-0">
                    <Text class="font-medium text-gray-900 truncate">{{doc.filename}}</Text>
                    <div class="flex items-center gap-3 text-sm text-gray-500">
                      <span>{{doc.employerName}}</span>
                      <span>•</span>
                      <span>{{this.formatDate doc.uploadedAt}}</span>
                      {{#if doc.paymentsCertified}}
                        <span>•</span>
                        <span class="text-emerald-600">{{doc.paymentsCertified}}
                          payments certified</span>
                      {{/if}}
                    </div>
                  </div>
                  <Chip @intent={{this.getStatusColor doc.status}} @size="sm">
                    {{doc.status}}
                  </Chip>
                  <button type="button" class="p-2 hover:bg-gray-100 rounded-lg">
                    <PhDownloadSimple @size={{20}} class="text-gray-500" />
                  </button>
                </div>
              {{/each}}
            </div>
          </div>
        </div>

        {{! Sidebar - Milestones }}
        <div class="space-y-6">
          {{! Milestone Tracker }}
          <div class="bg-white rounded-xl border border-gray-200 shadow-sm p-4">
            <SectionHeader @icon={{PhTarget}} @title="Milestones" @iconClass="text-violet-600" />

            <div class="space-y-3">
              {{#each this.progress.milestones as |milestone|}}
                <div class="flex items-center gap-3">
                  <div
                    class="w-8 h-8 rounded-full flex items-center justify-center
                      {{if milestone.reached 'bg-emerald-100' 'bg-gray-100'}}"
                  >
                    {{#if milestone.reached}}
                      <PhCheckCircle @size={{18}} class="text-emerald-600" />
                    {{else}}
                      <span class="text-xs font-medium text-gray-500">{{milestone.payments}}</span>
                    {{/if}}
                  </div>
                  <div class="flex-1">
                    <Text
                      class="text-sm
                        {{if milestone.reached 'text-emerald-700 font-medium' 'text-gray-600'}}"
                    >
                      {{milestone.label}}
                    </Text>
                    {{#if milestone.reachedDate}}
                      <Text class="text-xs text-gray-500">{{this.formatDate
                          milestone.reachedDate
                        }}</Text>
                    {{/if}}
                  </div>
                </div>
              {{/each}}
            </div>
          </div>

          {{! Employer Status }}
          <div class="bg-white rounded-xl border border-gray-200 shadow-sm p-4">
            <SectionHeader
              @icon={{PhShieldCheck}}
              @title="Employer Status"
              @iconClass="text-emerald-600"
            />

            <div class="space-y-3">
              <InfoRow @label="Employer" @value={{this.progress.currentEmployerName}} />
              <InfoRow @label="Qualifies?">
                <Chip
                  @intent={{if this.progress.currentEmployerQualifies "success" "danger"}}
                  @size="sm"
                >
                  {{if this.progress.currentEmployerQualifies "Yes" "No"}}
                </Chip>
              </InfoRow>
              <InfoRow
                @label="Start Date"
                @value={{this.formatDate this.progress.currentEmployerStartDate}}
              />
              <InfoRow @label="Last Certified">
                <Text class="font-medium">
                  {{#if this.progress.lastCertificationDate}}
                    {{this.formatDate this.progress.lastCertificationDate}}
                  {{else}}
                    Not yet
                  {{/if}}
                </Text>
              </InfoRow>
            </div>
          </div>

          {{! Quick Actions }}
          <div
            class="bg-gradient-to-br from-violet-50 to-purple-50 rounded-xl border border-violet-100 p-4"
          >
            <Heading @level={{3}} class="text-lg font-semibold mb-3">Quick Actions</Heading>
            <div class="space-y-2">
              <Button
                @intent="primary"
                @icon={{PhFileText}}
                @label="Generate ECF Form"
                class="w-full justify-center"
              />
              <LinkTo @route="authenticated.reimagined.pslf">
                <Button
                  @intent="default"
                  @appearance="outlined"
                  @icon={{PhArrowRight}}
                  @iconPlacement="end"
                  @label="View Full PSLF Dashboard"
                  class="w-full justify-center"
                />
              </LinkTo>
            </div>
          </div>
        </div>
      </div>
    </VStack>
  </template>
}

// Helper functions
function eq(a: unknown, b: unknown): boolean {
  return a === b;
}

function gte(a: number, b: number): boolean {
  return a >= b;
}
